import { useQuery } from "@tanstack/react-query";
import { Badge, Box, Card, Group, Stack, Table, Text } from "@mantine/core";
import { COLLEAGUES_GUIDES_KEY, guidesApi, type ColleaguesGuideProgress } from "@/lib/api/guides";
import { colleagueCellLabel } from "@/lib/guides";
import { useGuidesRealtime } from "@/hooks/use-guides";

/**
 * Company admins on `/colleagues`: how far each colleague is through the
 * guides, and the company's own steps. Hidden when there are no guides.
 */
export function ColleaguesGuideProgress() {
  useGuidesRealtime();
  const { data } = useQuery({
    queryKey: COLLEAGUES_GUIDES_KEY,
    queryFn: () => guidesApi.colleagues().then((r) => r.data),
    staleTime: 60_000,
  });
  if (!data || data.guides.length === 0) return null;
  return <ColleaguesGuideTable data={data} />;
}

export function ColleaguesGuideTable({ data }: { data: ColleaguesGuideProgress }) {
  return (
    <Card padding="lg" withBorder>
      <Stack gap="sm">
        <Box>
          <Text fw={600} fz="lg">
            Guide progress
          </Text>
          <Text fz="sm" c="dimmed">
            How far your colleagues are through the office's guides.
          </Text>
        </Box>
        <Table.ScrollContainer minWidth={Math.max(360, 200 + data.guides.length * 130)}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Colleague</Table.Th>
                {data.guides.map((g) => (
                  <Table.Th key={g.key}>{g.title}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.guides.some((g) => g.company.total > 0) && (
                <Table.Tr>
                  <Table.Td>
                    <Text fz="sm" fw={500}>
                      Company steps
                    </Text>
                  </Table.Td>
                  {data.guides.map((g) => (
                    <Table.Td key={g.key}>
                      {g.company.total === 0 ? (
                        <Text fz="sm" c="dimmed">
                          —
                        </Text>
                      ) : g.company.done === g.company.total ? (
                        <Badge size="sm" variant="light" color="teal">
                          Done
                        </Badge>
                      ) : (
                        <Text fz="sm">
                          {g.company.done}/{g.company.total}
                        </Text>
                      )}
                    </Table.Td>
                  ))}
                </Table.Tr>
              )}
              {data.members.map((m, i) => (
                <Table.Tr key={`${m.name}-${i}`}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text fz="sm" style={{ whiteSpace: "nowrap" }}>
                        {m.name}
                      </Text>
                      {m.is_admin && (
                        <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>
                          Admin
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  {data.guides.map((g) => {
                    const cell = m.cells[g.key];
                    const label = colleagueCellLabel(cell);
                    return (
                      <Table.Td key={g.key}>
                        {cell?.completed ? (
                          <Badge size="sm" variant="light" color="teal">
                            {label}
                          </Badge>
                        ) : (
                          <Text fz="sm" c={label === "—" ? "dimmed" : undefined}>
                            {label}
                          </Text>
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Card>
  );
}
